import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import GenericCrudTableModal from "@/components/tables/genericCrudTableModal";
import { api } from "@/helper/api";
import { useDispatch } from "react-redux";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { userEntityAdapter } from "@/components/userAdministration/userEntityAdapter";

interface UserEditModalProps {
  open: boolean;
  onClose: () => void;
  userSubjectId: string;
  companySubjectId?: string;
  onUserUpdated?: () => void;
  handleResetSubject?: () => void;
  handleSubjectId?: (id: string) => void;
}

const UserEditModal: FC<UserEditModalProps> = ({
  open,
  onClose,
  userSubjectId,
  companySubjectId,
  onUserUpdated,
  handleResetSubject,
  handleSubjectId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [userData, setUserData] = useState<any>();
  const { handleError, errorModal } = useErrorModal();

  const handleSetError = (error: any) => {
    handleError(error);
  };

  useEffect(() => {
    if (!open || !userSubjectId) return;

    const params: Record<string, any> = {
      subject_id: userSubjectId,
      depth: "0",
      include_relations: "true",
      departments: "true",
    };

    api.genericApiRequest({
      method: "get",
      entity: "user",
      parametersToRender: params,
      successHandler: (res: any) => {
        const data = res.data.results?.[0];
        if (!data) {
          handleError(t("user_not_found"));
          return;
        }

        const preparedUserdata = {
          ...data,
          birth_date: data.person?.birth_date,
          sex: data.person?.sex,
          phone: data.person?.phone,
          preferred_language: data.person?.preferred_language,
          worker_id: data.person?.worker_id,
        };
        setUserData(preparedUserdata);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  }, [open, userSubjectId]);

  const userEditFields = [
    {
      header: t("first_name"),
      accessorKey: "first_name",
      id: "first_name",
      required: true,
      enableForCreate: true,
    },
    {
      header: t("last_name"),
      accessorKey: "last_name",
      id: "last_name",
      required: true,
      enableForCreate: true,
    },
    {
      header: t("gender"),
      accessorKey: "person.sex",
      childEntityName: "person",
      customColumnFilterId: "person__sex",
      id: "sex",
      required: true,
      errorMessage: t("field_required"),
      enableForCreate: true,
      selectOptions: [
        {
          value: "male",
          label: t("male"),
        },
        {
          value: "female",
          label: t("female"),
        },
        {
          value: "diverse",
          label: t("diverse"),
        },
      ],
    },
    {
      header: t("email"),
      accessorKey: "email",
      id: "email",
      enableForCreate: true,
      required: true,
      errorMessage: t("field_required"),
      validate: true,
      errorMessageValidation: t("email_already_in_use"),
    },
    {
      header: t("phone"),
      accessorKey: "person.phone",
      childEntityName: "person",
      id: "phone",
      enableForCreate: true,
    },
    {
      header: t("departments"),
      accessorKey: "department_set",
      id: "department_set",
      enableForCreate: true,
      multiselect: true,
      entity: "department",
    },
    {
      header: t("birth_date"),
      accessorKey: "person.birth_date",
      childEntityName: "person",
      id: "birth_date",
      enableGlobalFilter: false,
      enableForCreate: true,
      datePicker: true,
      required: true,
      errorMessage: t("field_required"),
      validate: true,
      errorMessageValidation: t("birthday_not_today"),
    },
    {
      header: t("analog_user"),
      id: "is_analog",
      enableForCreate: true,
      accessorKey: "is_analog",
      selectOptions: [
        { value: false, label: t("no") },
        { value: true, label: t("yes") },
      ],
    },
    {
      header: t("preferred_language"),
      id: "preferred_language",
      childEntityName: "person",
      accessorKey: "person.preferred_language",
      enableForCreate: true,
      selectOptions: [
        {
          value: "de",
          label: t("language_german"),
        },
        {
          value: "en",
          label: t("language_english"),
        },
      ],
    },
    {
      header: t("worker_id"),
      accessorKey: "person.worker_id",
      childEntityName: "person",
      id: "worker_id",
      enableForCreate: true,
      enableGlobalFilter: false,
    },
    {
      header: t("user_is_registered"),
      accessorKey: "is_registered",
      id: "is_registered",
      enableForCreate: true,
      hideField: true,
    },
    {
      accessorKey: "is_portal_registered",
      id: "is_portal_registered",
      enableForCreate: true,
      hideField: true,
    },
  ];

  const handleSubmit = async (data: any) => {
    const isSettingAnalog =
      data.is_analog === true || data.is_analog === "true";
    const wasAnalog =
      userData?.is_analog === true || userData?.is_analog === "true";
    const isConvertingRegisteredUserToAnalog =
      userData?.is_portal_registered === true && isSettingAnalog && !wasAnalog;

    if (isConvertingRegisteredUserToAnalog) {
      handleError(t("analog_cannot_set_for_registered_user"));
      return;
    }

    const submitData = {
      ...data,
      department_set: Array.isArray(data.department_set)
        ? data.department_set
            .map((item: any) => {
              if (typeof item === "object" && item !== null) {
                return item.id ?? item.value;
              }
              if (typeof item === "string") {
                return item;
              }
              return null;
            })
            .filter((id: any) => id != null)
        : [],
    };

    api.genericApiRequest({
      method: "put",
      entity: "user",
      entityId: userData.id,
      submitData: submitData,
      successHandler: (res: any) => {
        if (handleSubjectId && res?.data?.subject_id) {
          handleSubjectId(res.data.subject_id);
        }
        if (res?.data?.subject_id && res?.data?.name) {
          try {
            dispatch(
              setSubjectSearch({
                value: res.data.subject_id,
                label: res.data.name,
              }),
            );
          } catch (e) {
            console.error(e);
          }
        }
        onUserUpdated?.();
        onClose();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  return (
    <>
      {userData && (
        <GenericCrudTableModal
          open={open}
          handleError={handleSetError}
          columns={userEditFields}
          onClose={onClose}
          presetValues={userData}
          companySubjectId={companySubjectId}
          modalMode="edit"
          title={t("edit_employee_information")}
          submitButtonText={t("save")}
          entityOfTable="user"
          entityAdapter={userEntityAdapter}
          onSubmit={async (values: any) => {
            handleResetSubject?.();
            await handleSubmit(values);
          }}
        />
      )}
      {errorModal}
    </>
  );
};

export default UserEditModal;
